import { z } from 'zod';
import type { AnkaufKatalogEintrag } from './ankaufKatalog';

/* ------------------------------------------------------------ Begriffe (wie lib_ankauf.php) */

/**
 * Status einer Anfrage – die ersten Werte wie ank_status_alle() der PHP-Middleware, danach der Ablauf aus dem
 * Briefing „Erweiterung Ankauf“ (KYC, Abholung, Wareneingang, Werkstatt, Auszahlung). Reihenfolge = Ablauf.
 */
export const ANKAUF_STATUS = {
  neu: 'Neu',
  in_pruefung: 'In Prüfung',
  infos_angefordert: 'Infos angefordert',
  angebot: 'Angebot gesendet',
  gegenangebot: 'Gegenangebot erhalten',
  abgelaufen: 'Angebot abgelaufen',
  angenommen: 'Angenommen',
  kyc_offen: 'Identitätsprüfung läuft',
  kyc_bestanden: 'Identität geprüft',
  kyc_abgelehnt: 'Identitätsprüfung fehlgeschlagen',
  abholung_geplant: 'Abholung geplant',
  eingegangen: 'Uhr eingegangen',
  wareneingang_pruefung: 'Prüfung im Wareneingang',
  kva_offen: 'Kostenvoranschlag offen',
  kva_freigegeben: 'Kostenvoranschlag freigegeben',
  kva_abgelehnt: 'Kostenvoranschlag abgelehnt',
  endpreis: 'Endpreis angeboten',
  uhr_akzeptiert: 'Uhr akzeptiert',
  nachverhandlung: 'Nachverhandlung',
  zurueckgesendet: 'Zurückgesendet',
  gelistet: 'In Kommission gelistet',
  verkauft: 'Verkauft',
  auszahlung_offen: 'Auszahlung offen',
  ausgezahlt: 'Ausgezahlt',
  auszahlung_fehlgeschlagen: 'Auszahlung fehlgeschlagen',
  abgelehnt: 'Abgelehnt',
  erledigt: 'Erledigt',
  storniert: 'Storniert',
  spam: 'Spam',
} as const;
export type AnkaufStatus = keyof typeof ANKAUF_STATUS;
export const ANKAUF_STATUS_CODES = Object.keys(ANKAUF_STATUS) as [AnkaufStatus, ...AnkaufStatus[]];
export const AnkaufStatusSchema = z.enum(ANKAUF_STATUS_CODES);

/**
 * Erlaubte Übergänge – serverseitig erzwungen (uebergang() in apps/api/src/lib/ankauf.ts), jeder Wechsel steht mit
 * Akteur und Zeit im Verlauf. Wer einen Übergang auslösen darf, regeln die Aktionen (ankaufMoeglich, ankaufManuell).
 *
 * Grundsatz 4 des Briefings: kyc_offen ist nur aus angenommen erreichbar; Abholung nur nach bestandenem KYC,
 * Auszahlung nur nach Annahme der Uhr (Direktankauf) bzw. Verkauf (Kommission). „storniert“ geht jederzeit außer
 * während einer laufenden oder nach einer erfolgten Auszahlung.
 */
const STORNO = 'storniert' as const;
export const ANKAUF_UEBERGAENGE: Record<AnkaufStatus, readonly AnkaufStatus[]> = {
  neu: ['in_pruefung', 'infos_angefordert', 'angebot', 'abgelehnt', STORNO, 'spam'],
  in_pruefung: ['infos_angefordert', 'angebot', 'abgelehnt', STORNO, 'spam'],
  infos_angefordert: ['in_pruefung', 'angebot', 'abgelehnt', STORNO, 'spam'],
  angebot: ['angebot', 'gegenangebot', 'angenommen', 'abgelehnt', 'abgelaufen', STORNO],
  gegenangebot: ['angebot', 'angenommen', 'abgelehnt', STORNO],
  abgelaufen: ['angebot', 'gegenangebot', 'abgelehnt', STORNO],
  // erledigt: Abschluss von Hand, solange der Ablauf nach der Annahme nicht im Modul läuft (Phase 2–4)
  angenommen: ['kyc_offen', 'erledigt', STORNO],
  kyc_offen: ['kyc_bestanden', 'kyc_abgelehnt', STORNO],
  kyc_bestanden: ['abholung_geplant', STORNO],
  kyc_abgelehnt: [],
  abholung_geplant: ['eingegangen', STORNO],
  eingegangen: ['wareneingang_pruefung', STORNO],
  wareneingang_pruefung: ['kva_offen', 'endpreis', 'uhr_akzeptiert', 'zurueckgesendet', STORNO],
  kva_offen: ['kva_freigegeben', 'kva_abgelehnt', STORNO],
  kva_freigegeben: ['endpreis', 'uhr_akzeptiert', 'zurueckgesendet', STORNO],
  kva_abgelehnt: ['endpreis', 'uhr_akzeptiert', 'zurueckgesendet', STORNO],
  // Endpreis nennt die Werkstatt nach der Prüfung; der Verkäufer nimmt an, verhandelt nach oder lässt die Uhr zurückschicken
  endpreis: ['uhr_akzeptiert', 'nachverhandlung', 'zurueckgesendet', STORNO],
  uhr_akzeptiert: ['auszahlung_offen', 'gelistet', STORNO],
  nachverhandlung: ['endpreis', 'zurueckgesendet', STORNO],
  zurueckgesendet: ['erledigt'],
  gelistet: ['verkauft', 'zurueckgesendet', STORNO],
  verkauft: ['auszahlung_offen'],
  auszahlung_offen: ['ausgezahlt', 'auszahlung_fehlgeschlagen'],
  ausgezahlt: ['erledigt'],
  auszahlung_fehlgeschlagen: ['auszahlung_offen', STORNO],
  abgelehnt: ['in_pruefung'],
  erledigt: [],
  storniert: [],
  spam: ['neu'],
};

export function ankaufUebergangErlaubt(alt: AnkaufStatus, neu: AnkaufStatus): boolean {
  return ANKAUF_UEBERGAENGE[alt].includes(neu);
}

/** Verhandlung läuft: Angebote, Gegenangebote und Rückfragen sind möglich */
export const ANKAUF_VERHANDLUNG: readonly AnkaufStatus[] = ['neu', 'in_pruefung', 'infos_angefordert', 'angebot', 'gegenangebot', 'abgelaufen'];

/** Verhandlung beendet (angenommen, abgelehnt, abgeschlossen …): nur noch Nachrichten möglich */
export const ANKAUF_ENDE: readonly AnkaufStatus[] = ANKAUF_STATUS_CODES.filter((s) => !ANKAUF_VERHANDLUNG.includes(s));

/** Endstatus ohne weiteren Übergang – dort wartet niemand mehr */
export const ANKAUF_ABGESCHLOSSEN: readonly AnkaufStatus[] = ANKAUF_STATUS_CODES.filter((s) => s !== 'spam' && ANKAUF_UEBERGAENGE[s].length === 0).concat(['abgelehnt']);

/**
 * Status, die das Team von Hand setzen darf (Konto → Status und interne Notiz). Alles Übrige ergibt sich aus Aktionen.
 * Stornieren verlangt einen Grund.
 */
const MANUELL: readonly AnkaufStatus[] = ['neu', 'in_pruefung', 'erledigt', 'storniert', 'spam'];
export function ankaufManuell(alt: AnkaufStatus): AnkaufStatus[] {
  return ANKAUF_UEBERGAENGE[alt].filter((s) => MANUELL.includes(s));
}

export type AnkaufWartet = '' | 'team' | 'kunde';

export const ANKAUF_ZUSTAND = { neu: 'Neu', wie_neu: 'Wie neu', gut: 'Gut', gebraucht: 'Gebraucht' } as const;
export type AnkaufZustand = keyof typeof ANKAUF_ZUSTAND;

export const ANKAUF_METHODEN = { kommission: 'Kommission', direktverkauf: 'Direktverkauf', inzahlungnahme: 'Tausch / Inzahlungnahme' } as const;
export type AnkaufMethode = keyof typeof ANKAUF_METHODEN;
export const ANKAUF_METHODE_CODES = Object.keys(ANKAUF_METHODEN) as [AnkaufMethode, ...AnkaufMethode[]];

/** Sprachen der Verkäufer: Portal und E-Mails gibt es auf Deutsch und Englisch, die übrigen fallen auf Englisch zurück */
export const ANKAUF_SPRACHEN = { de: 'Deutsch', en: 'English', nl: 'Nederlands', fr: 'Français', it: 'Italiano' } as const;
export type AnkaufSprache = keyof typeof ANKAUF_SPRACHEN;
export const ANKAUF_SPRACHE_CODES = Object.keys(ANKAUF_SPRACHEN) as [AnkaufSprache, ...AnkaufSprache[]];
/** Sprachen mit eigenen Texten (Portal, Mail-Vorlagen) */
export const ANKAUF_TEXT_SPRACHEN = ['de', 'en'] as const;
export type AnkaufTextSprache = (typeof ANKAUF_TEXT_SPRACHEN)[number];
export const ankaufTextSprache = (s: string | null | undefined): AnkaufTextSprache => (s === 'de' ? 'de' : s && s in ANKAUF_SPRACHEN ? 'en' : 'de');

export const ANKAUF_WAEHRUNGEN = ['EUR', 'USD', 'CHF', 'GBP'] as const;
export type AnkaufWaehrung = (typeof ANKAUF_WAEHRUNGEN)[number];

/** search = Suche im Katalog, steps = Marke → Modell → Referenz, manual = freie Eingabe */
export const ANKAUF_AUSWAHL = { search: 'Suche', steps: 'Marke/Modell/Referenz', manual: 'Manuelle Eingabe' } as const;
export type AnkaufAuswahl = keyof typeof ANKAUF_AUSWAHL;

export const ANKAUF_FOTO_SLOTS = { vorderseite: 'Vorderseite', rueckseite: 'Rückseite', armband: 'Armband', papiere: 'Originalpapiere', foto: 'Foto' } as const;
export type AnkaufFotoSlot = keyof typeof ANKAUF_FOTO_SLOTS;
export const ANKAUF_FOTO_TYPEN = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'] as const;
export const ANKAUF_FOTO_MAX_BYTES = 10 * 1024 * 1024;
export const ANKAUF_FOTO_MAX_ANZAHL = 8;
/** Fotos zur Begutachtung: Details bleiben erkennbar (ANK_FOTO_KANTE) */
export const ANKAUF_FOTO_KANTE = 2400;

/** Gültigkeit eines Angebots ohne eigenes Datum (ANK_VH_GUELTIG_TAGE) */
export const ANKAUF_GUELTIG_TAGE = 14;

export const ANKAUF_MARKEN_STANDARD = [
  'A. Lange & Söhne', 'Audemars Piguet', 'Breguet', 'Breitling', 'Bulgari', 'Cartier', 'Chopard', 'Hublot', 'IWC',
  'Jaeger-LeCoultre', 'Omega', 'Panerai', 'Patek Philippe', 'Richard Mille', 'Rolex', 'Tag Heuer', 'Tudor', 'Vacheron Constantin', 'Zenith',
];

export const ANKAUF_BETREFF_STANDARD = 'Ihre Verkaufsanfrage {nummer}';
export const ANKAUF_TEXT_STANDARD =
  'Guten Tag {anrede} {nachname},\n\n' +
  'vielen Dank für Ihre Anfrage {nummer} zum Verkauf Ihrer {marke} {modell} {referenz}.\n' +
  'Unser Team prüft Ihre Angaben und meldet sich innerhalb von 48 Stunden mit einem persönlichen Angebot bei Ihnen.\n\n' +
  'Den Stand Ihrer Anfrage und unser Angebot sehen Sie jederzeit hier:\n{link}\n\n' +
  'Mit freundlichen Grüßen';
/** Platzhalter in Betreff und Text der Bestätigung */
export const ANKAUF_PLATZHALTER = ['{anrede}', '{vorname}', '{nachname}', '{nummer}', '{marke}', '{modell}', '{referenz}', '{betreiber}', '{link}'] as const;

/* ------------------------------------------------------------ Verhandlung */

export const ANKAUF_AKTIONEN = {
  angebot: 'Angebot',
  gegenangebot: 'Gegenangebot',
  annehmen: 'Annehmen',
  ablehnen: 'Ablehnen',
  nachricht: 'Nachricht',
  infos: 'Infos anfordern',
  nachreichen: 'Infos nachreichen',
} as const;
export type AnkaufAktion = keyof typeof ANKAUF_AKTIONEN;
export const ANKAUF_AKTION_CODES = Object.keys(ANKAUF_AKTIONEN) as [AnkaufAktion, ...AnkaufAktion[]];
export type AnkaufEintragArt = AnkaufAktion | 'eingang' | 'ablauf' | 'status' | 'notiz';

/**
 * Welche Aktionen sind für Team bzw. Kunde gerade möglich? (ank_vh_moeglich, erweitert um Rückfragen)
 * Das Team kann vor einem Angebot fehlende Angaben anfordern; der Verkäufer reicht sie mit Text und Fotos nach.
 */
export function ankaufMoeglich(status: AnkaufStatus, wer: 'team' | 'kunde'): AnkaufAktion[] {
  if (status === 'spam') return [];
  if (ANKAUF_ENDE.includes(status)) return status === 'storniert' ? [] : ['nachricht'];
  if (wer === 'team') {
    if (status === 'gegenangebot') return ['annehmen', 'angebot', 'ablehnen', 'nachricht'];
    if (status === 'neu' || status === 'in_pruefung') return ['angebot', 'infos', 'ablehnen', 'nachricht'];
    return ['angebot', 'ablehnen', 'nachricht'];
  }
  if (status === 'angebot') return ['annehmen', 'gegenangebot', 'ablehnen', 'nachricht'];
  if (status === 'abgelaufen') return ['gegenangebot', 'nachricht'];
  if (status === 'infos_angefordert') return ['nachreichen', 'nachricht'];
  return ['nachricht'];
}

/** Betrag aus Eingaben wie „8.500“, „8.500,50“, „8500.50“ oder „8500“ (ank_betrag) */
export function betragLesen(x: unknown): number {
  let s = String(x ?? '').replace(/[^0-9,.]/g, '');
  if (s === '') return 0;
  if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
  else if ((s.match(/\./g) ?? []).length > 1 || /\.\d{3}$/.test(s)) s = s.replace(/\./g, '');
  const n = Number(s);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
}

export function geldText(betrag: number, waehrung: string): string {
  return `${betrag.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${waehrung === 'EUR' ? '€' : waehrung}`;
}

/* ------------------------------------------------------------ Öffentliches Anfrageformular */

const t = (max: number) => z.string().trim().max(max).default('');
const ja = z.preprocess((v) => (v === 'ja' || v === 'true' || v === '1' ? true : v === 'nein' || v === 'false' || v === '0' ? false : v), z.boolean());
const HttpUrl = z.union([z.string().trim().max(500).regex(/^https?:\/\/[^\s"<>]+$/i, 'Bitte eine gültige Adresse angeben'), z.literal('')]).default('');

const Kurz = z.string().trim().max(200).default('');
/** Tracking-Felder der Anfrage (Payload-Feld tracking) */
export const AnkaufTrackingSchema = z
  .object({
    ga4ClientId: Kurz,
    ga4SessionId: Kurz,
    gclid: Kurz,
    utmSource: Kurz,
    utmMedium: Kurz,
    utmCampaign: Kurz,
    utmContent: Kurz,
    seite: z.string().trim().max(500).default(''),
  })
  .strip();
export type AnkaufTracking = z.output<typeof AnkaufTrackingSchema> & { host?: string; browser?: string };

/** Eingaben des Formulars (ank_eingabe_pruefen). fotos = Schlüssel der vorher hochgeladenen Fotos. */
export const AnkaufAnfrageSchema = z
  .object({
    anrede: z.enum(['herr', 'frau'], { errorMap: () => ({ message: 'Bitte die Anrede wählen.' }) }),
    vorname: z.string().trim().min(1, 'Bitte den Vornamen angeben.').max(80),
    nachname: z.string().trim().min(1, 'Bitte den Nachnamen angeben.').max(80),
    email: z.string().trim().toLowerCase().email('Bitte eine gültige E-Mail-Adresse angeben.').max(190),
    telefon: z
      .string()
      .trim()
      .max(40)
      .transform((v) => v.replace(/[^0-9+ ()/-]/g, ''))
      .refine((v) => v.replace(/\D/g, '').length >= 6, 'Bitte eine gültige Telefonnummer angeben.'),
    land: z.string().trim().toUpperCase().regex(/^[A-Z]{2}$/, 'Bitte das Land wählen.'),
    waehrung: z.enum(ANKAUF_WAEHRUNGEN).default('EUR'),
    newsletter: z.boolean().default(false),
    marke: z.string().trim().min(1, 'Bitte die Marke angeben.').max(80),
    modell: t(120),
    referenz: t(80),
    sku: t(80),
    auswahl: z.enum(['search', 'steps', 'manual']).default('manual'),
    papiere: ja.nullable().default(null).refine((v) => v !== null, 'Bitte angeben, ob die Originalpapiere vorhanden sind.'),
    box: ja.nullable().default(null).refine((v) => v !== null, 'Bitte angeben, ob die Originalbox vorhanden ist.'),
    baujahr: z.union([z.string().regex(/^(19[5-9]\d|20\d\d)$/, 'Bitte ein Jahr zwischen 1950 und heute angeben.'), z.literal('')]).default(''),
    zustand: z.enum(Object.keys(ANKAUF_ZUSTAND) as [AnkaufZustand, ...AnkaufZustand[]], { errorMap: () => ({ message: 'Bitte den Zustand angeben.' }) }),
    anmerkungen: t(2000),
    methoden: z.array(z.enum(Object.keys(ANKAUF_METHODEN) as [AnkaufMethode, ...AnkaufMethode[]])).max(3).default([]),
    gesucht: HttpUrl,
    bildUrl: HttpUrl,
    fotos: z.array(z.string().regex(/^[A-Za-z0-9_-]{32}$/)).max(ANKAUF_FOTO_MAX_ANZAHL).default([]),
    /** Sprache des Verkäufers für Portal und E-Mails; ohne Angabe Deutsch */
    sprache: z.preprocess((v) => (typeof v === 'string' ? v.trim().toLowerCase().slice(0, 2) : v), z.enum(ANKAUF_SPRACHE_CODES)).default('de'),
    /** Preisvorstellung des Verkäufers (optional) */
    preisvorstellung: z.union([z.string().max(30), z.number()]).optional(),
    /** Herkunft für die Auswertung (GA4, Kampagne, Seite) – Host und Browser ergänzt der Server */
    tracking: AnkaufTrackingSchema.default({}),
    /** Honeypot – Menschen lassen das Feld leer */
    website: z.string().max(500).default(''),
    turnstile: z.string().max(4000).default(''),
  })
  .strip();
export type AnkaufAnfrageEingabe = z.input<typeof AnkaufAnfrageSchema>;
export type AnkaufAnfrageDaten = z.output<typeof AnkaufAnfrageSchema>;

/** Was das Formular vor dem Absenden wissen muss (ankauf.php?r=konfig) */
export interface AnkaufKonfig {
  name: string;
  marken: string[];
  methoden: AnkaufMethode[];
  turnstile: string;
  fotosPflichtManuell: boolean;
  maxMb: number;
  maxFotos: number;
  /** Anfragen werden angenommen (Formular freigeschaltet bzw. Shop-Domain eingetragen) */
  senden: boolean;
  /** aktive Einträge im Uhren-Katalog – 0 = nur freie Eingabe */
  katalog: number;
}

export interface AnkaufFotoHochgeladen {
  schluessel: string;
}

/* ------------------------------------------------------------ Gespeicherte Angaben */

/** Angaben zur Ware im Klartext (Spalte angaben) */
export interface AnkaufAngaben {
  sku?: string;
  zustand?: AnkaufZustand | '';
  papiere?: boolean | null;
  box?: boolean | null;
  baujahr?: string;
  methoden?: AnkaufMethode[];
  gesucht?: string;
  bildUrl?: string;
  newsletter?: boolean;
  /** Eintrag im Uhren-Katalog, aus dem Marke, Modell und Referenz stammen */
  katalogId?: string;
  /** Preisvorstellung des Verkäufers */
  preisvorstellung?: number;
}

/** Verschlüsselte Kontaktdaten (Spalte kunde) */
export interface AnkaufKunde {
  anrede: 'herr' | 'frau';
  vorname: string;
  nachname: string;
  email: string;
  telefon: string;
  land: string;
  anmerkungen: string;
}

/* ------------------------------------------------------------ Konto: Liste und Detail */

/** offen = zu bearbeiten (neu, in Prüfung oder wartet auf das Team), wartet = Kunde hat geantwortet */
export const ANKAUF_FILTER = { offen: 'Zu bearbeiten', wartet: 'Wartet auf uns' } as const;
export type AnkaufFilter = keyof typeof ANKAUF_FILTER;

const Datum = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const AnkaufAbfrage = z.object({
  q: z.string().trim().max(100).optional(),
  status: z.union([AnkaufStatusSchema, z.enum(['offen', 'wartet'])]).optional(),
  /** Ankaufart: vereinbart bzw. vom Verkäufer gewünscht */
  ankaufart: z.enum(ANKAUF_METHODE_CODES).optional(),
  marke: z.string().trim().max(80).optional(),
  /** Eingang von/bis (Kalendertage, deutsche Zeit) */
  von: Datum.optional(),
  bis: Datum.optional(),
  cursor: z.string().max(400).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type AnkaufAbfrage = z.input<typeof AnkaufAbfrage>;

export interface AnkaufZeile {
  id: string;
  nummer: string;
  status: AnkaufStatus;
  wartetAuf: AnkaufWartet;
  marke: string;
  modell: string;
  referenz: string;
  auswahl: AnkaufAuswahl;
  name: string;
  land: string;
  zustand: string;
  fotos: number;
  betrag: number | null;
  /** Obergrenze, wenn das Angebot eine Spanne ist */
  betragBis: number | null;
  waehrung: AnkaufWaehrung;
  /** vereinbarte bzw. angebotene Ankaufart, sonst die erste gewünschte */
  ankaufart: AnkaufMethode | null;
  sprache: AnkaufSprache;
  erstelltAm: string;
}

export type AnkaufZaehler = Record<AnkaufStatus | AnkaufFilter | 'alle', number>;

/** Kennzahlen über der Inbox: laufender Monat (deutsche Zeit) */
export interface AnkaufKennzahlen {
  /** abrechenbare Anfragen im laufenden Monat (ankauf_monat) */
  monat: number;
  /** angenommene Anfragen im laufenden Monat und deren vereinbarte Summe in EUR */
  angenommenMonat: number;
  volumenMonat: number;
  /** Annahmequote der letzten 90 Tage: angenommen / (angenommen + abgelehnt + abgelaufen), 0–1; null = noch keine */
  quote: number | null;
}

export interface AnkaufSeite {
  eintraege: AnkaufZeile[];
  weiter: string | null;
  gesamt: number;
  /** je Status über alle Anfragen; alle = ohne Spam */
  zaehler: AnkaufZaehler;
  kennzahlen: AnkaufKennzahlen;
}

export interface AnkaufEintrag {
  id: string;
  von: 'team' | 'kunde' | 'system';
  art: AnkaufEintragArt;
  betrag: number | null;
  /** Spanne: Obergrenze des Angebots */
  betragBis: number | null;
  ankaufart: AnkaufMethode | null;
  /** Statuswechsel */
  statusAlt: AnkaufStatus | null;
  statusNeu: AnkaufStatus | null;
  gueltigBis: string | null;
  text: string;
  name: string;
  zeitpunkt: string;
}

export interface AnkaufFoto {
  id: string;
  slot: string;
  name: string;
  mime: string;
  groesse: number;
}

export interface AnkaufDetail extends AnkaufZeile {
  kunde: AnkaufKunde;
  angaben: AnkaufAngaben;
  quelle: string;
  gueltigBis: string | null;
  fotoListe: AnkaufFoto[];
  verlauf: AnkaufEintrag[];
  moeglich: AnkaufAktion[];
  /** Status, die das Team von Hand setzen kann */
  manuell: AnkaufStatus[];
  /** letztes Angebot bzw. Gegenangebot */
  angebot: AnkaufEintrag | null;
  /** vom Verkäufer gewünschte und vom Marktplatz freigegebene Ankaufarten */
  ankaufarten: AnkaufMethode[];
  tracking: AnkaufTracking;
  geaendertAm: string;
  /** passender Eintrag im Uhren-Katalog (auch inaktiv), sonst null */
  katalog: AnkaufKatalogEintrag | null;
}

export const AnkaufVerhandelnSchema = z.object({
  aktion: z.enum(ANKAUF_AKTION_CODES),
  betrag: z.union([z.string().max(30), z.number()]).optional(),
  /** nur Angebot des Teams: Obergrenze, wenn eine Spanne angeboten wird (vorläufiges Angebot nach Fotos) */
  betragBis: z.union([z.string().max(30), z.number()]).optional(),
  /** nur Angebot des Teams; leer = gewünschte Ankaufart des Verkäufers */
  ankaufart: z.enum(ANKAUF_METHODE_CODES).optional(),
  /** nur Angebot des Teams; leer = heute + 14 Tage */
  gueltigBis: z.union([z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Bitte ein Datum angeben'), z.literal('')]).optional(),
  text: z.string().max(3000).default(''),
});
export type AnkaufVerhandelnEingabe = z.input<typeof AnkaufVerhandelnSchema>;

export const AnkaufStatusAendernSchema = z
  .object({
    status: AnkaufStatusSchema.optional(),
    notiz: z.string().trim().max(2000).default(''),
    /** Stornieren: Verkäufer per E-Mail informieren (Grund steht in der Notiz) */
    mail: z.boolean().default(false),
  })
  .refine((d) => d.status || d.notiz, { message: 'Bitte einen Status wählen oder eine Notiz eingeben.', path: ['notiz'] })
  .refine((d) => d.status !== 'storniert' || d.notiz, { message: 'Bitte einen Grund für die Stornierung angeben.', path: ['notiz'] });

export const AnkaufKundenlinkSchema = z.object({ senden: z.boolean().default(false) });

export interface AnkaufKundenlink {
  link: string;
  /** null = nicht verschickt */
  gesendet: boolean | null;
}

export interface AnkaufAktionErgebnis {
  detail: AnkaufDetail;
  /** E-Mail an die Gegenseite verschickt */
  mail: boolean;
}

/* ------------------------------------------------------------ Kundensicht (Verkäuferportal und alter persönlicher Link) */

export interface AnkaufKundenSicht {
  id: string;
  nummer: string;
  erstelltAm: string;
  status: AnkaufStatus;
  statusText: string;
  uhr: { marke: string; modell: string; referenz: string; bild: string; zustand: string; papiere: boolean | null; box: boolean | null; jahr: string };
  /** url: im Portal signiert und kurz gültig (Bilder laden ohne Kopfzeile); beim alten Link leer */
  fotos: { id: string; slot: string; url: string }[];
  /** „Frau Beispiel“ */
  name: string;
  waehrung: AnkaufWaehrung;
  sprache: AnkaufSprache;
  angebot: AnkaufEintrag | null;
  verlauf: AnkaufEintrag[];
  aktionen: AnkaufAktion[];
  /** offene Rückfrage des Teams (Infos angefordert) */
  rueckfrage: string | null;
  betreiber: string;
}

export const AnkaufKundeSchema = z.object({ z: z.string().regex(/^[A-Za-z0-9_-]{32}$/) });
export const AnkaufKundeAktionSchema = AnkaufKundeSchema.merge(AnkaufVerhandelnSchema.omit({ gueltigBis: true }));
export const AnkaufLinkAnfordernSchema = z.object({ email: z.string().trim().toLowerCase().max(190) });

/* ------------------------------------------------------------ Verkäuferportal (Anmeldung per Einmal-Link) */

/** Einmal-Token und Sitzungsschlüssel: 24 Zufallsbytes, base64url */
export const ANKAUF_TOKEN_MUSTER = /^[A-Za-z0-9_-]{32}$/;
/** Alte persönliche Links (Etappe 3 und PHP-Middleware) gelten noch bis zu diesem Tag, danach nur noch Anmeldung per E-Mail */
export const ANKAUF_ALTLINK_BIS = '2027-02-05';
/** Anmelde-Link aus „Link anfordern“: 30 Minuten; Sitzung im Portal: 30 Tage */
export const ANKAUF_ANMELDUNG_MINUTEN = 30;
export const ANKAUF_SITZUNG_TAGE = 30;

export const AnkaufPortalAnmeldenSchema = z.object({
  email: z.string().trim().toLowerCase().max(190),
  sprache: z.enum(ANKAUF_SPRACHE_CODES).optional(),
});
export const AnkaufTokenEinloesenSchema = z.object({ t: z.string().regex(ANKAUF_TOKEN_MUSTER) });

export interface AnkaufPortalSitzung {
  sitzung: string;
  gueltigBis: string;
  /** Link aus einer Prozess-Mail: diese Anfrage öffnen */
  anfrageId: string | null;
  /** Das Angebot aus der Mail ist bereits beantwortet (angenommen, abgelehnt, ersetzt, abgelaufen) */
  beantwortet: boolean;
}

export interface AnkaufPortalZeile {
  id: string;
  nummer: string;
  status: AnkaufStatus;
  marke: string;
  modell: string;
  referenz: string;
  bild: string;
  betrag: number | null;
  betragBis: number | null;
  waehrung: AnkaufWaehrung;
  /** Ihre Antwort ist gefragt (Angebot, Rückfrage) */
  wartetAufSie: boolean;
  erstelltAm: string;
}

export interface AnkaufPortalUebersicht {
  name: string;
  sprache: AnkaufSprache;
  betreiber: string;
  anfragen: AnkaufPortalZeile[];
}

export const AnkaufPortalAktionSchema = z.object({
  aktion: z.enum(['annehmen', 'gegenangebot', 'ablehnen', 'nachricht', 'nachreichen']),
  betrag: z.union([z.string().max(30), z.number()]).optional(),
  text: z.string().max(3000).default(''),
  /** Nachreichen: Schlüssel vorher hochgeladener Fotos */
  fotos: z.array(z.string().regex(ANKAUF_TOKEN_MUSTER)).max(ANKAUF_FOTO_MAX_ANZAHL).default([]),
});

/* ------------------------------------------------------------ Upload-Adressen (Eingang aus dem Shop) */

/** Kurzlebige Upload-Adresse: 15 Minuten, ein Foto */
export const ANKAUF_UPLOAD_MINUTEN = 15;
export const AnkaufUploadAnfordernSchema = z.object({
  slot: z.string().trim().toLowerCase().max(30).default('foto'),
  dateiname: z.string().max(200).default(''),
});
export interface AnkaufUploadAdresse {
  /** PUT mit den Rohdaten des Fotos (Content-Type image/…) */
  url: string;
  /** beim Absenden der Anfrage in fotos[] mitschicken */
  schluessel: string;
  gueltigBis: string;
}

/* ------------------------------------------------------------ Einstellungen */

const Liste = (max: number) =>
  z.preprocess((v) => (typeof v === 'string' ? v.split(/[\s,;]+/) : v), z.array(z.string().trim()).max(max)).transform((l) => [...new Set(l.filter(Boolean))]);

/** Host einer Domain-Angabe normalisieren (https://www.Shop.de/ → www.shop.de), sonst '' (ank_host) */
export function ankaufHost(d: string): string {
  const h = d
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/[/?#].*$/, '')
    .replace(/:\d+$/, '');
  return /^(\*\.)?[a-z0-9.-]+\.[a-z]{2,}$/.test(h) ? h : '';
}

export const AnkaufEinstellungenSchema = z.object({
  domains: Liste(50).superRefine((l, ctx) => {
    const falsch = l.find((d) => !ankaufHost(d));
    if (falsch) ctx.addIssue({ code: 'custom', message: `Ungültige Domain: ${falsch}` });
  }).transform((l) => [...new Set(l.map(ankaufHost))]),
  teamMails: Liste(20).superRefine((l, ctx) => {
    const falsch = l.find((m) => !z.string().email().safeParse(m).success);
    if (falsch) ctx.addIssue({ code: 'custom', message: `Ungültige E-Mail-Adresse: ${falsch}` });
  }).transform((l) => l.map((m) => m.toLowerCase())),
  bestaetigung: z.boolean().default(true),
  betreff: z.string().trim().max(200).default(''),
  text: z.string().trim().max(5000).default(''),
  markenManuell: z.preprocess((v) => (typeof v === 'string' ? v.split(/\r?\n/) : v), z.array(z.string().trim().max(80)).max(500)).transform((l) => [...new Set(l.filter(Boolean))]),
  methoden: z.array(z.enum(Object.keys(ANKAUF_METHODEN) as [AnkaufMethode, ...AnkaufMethode[]])).min(1, 'Bitte mindestens eine Verkaufsmethode freigeben.'),
  fotosPflichtManuell: z.boolean().default(true),
  formularAktiv: z.boolean().default(false),
  /** Shop-Section muss den öffentlichen Schlüssel mitschicken (Kopf x-ankauf-schluessel) */
  schluesselPflicht: z.boolean().default(false),
  /** neuen öffentlichen Schlüssel erzeugen (der alte gilt sofort nicht mehr) */
  schluesselNeu: z.boolean().optional(),
  /** Gültigkeit von Angeboten und Links in Prozess-Mails */
  angebotTage: z.coerce.number().int().min(1).max(90).default(ANKAUF_GUELTIG_TAGE),
  linkTage: z.coerce.number().int().min(1).max(30).default(7),
  turnstileSite: z.string().trim().max(120).default(''),
  /** leer = bisheriges Geheimnis behalten */
  turnstileGeheimnis: z.string().trim().max(200).optional(),
  turnstileLoeschen: z.boolean().optional(),
});
export type AnkaufEinstellungenEingabe = z.input<typeof AnkaufEinstellungenSchema>;

export interface AnkaufEinstellungen {
  domains: string[];
  teamMails: string[];
  bestaetigung: boolean;
  betreff: string;
  text: string;
  markenManuell: string[];
  methoden: AnkaufMethode[];
  fotosPflichtManuell: boolean;
  formularAktiv: boolean;
  /** öffentlicher Schlüssel des Marktplatzes für die Shop-Section – kein Geheimnis, darf im Theme stehen */
  schluessel: string;
  schluesselPflicht: boolean;
  angebotTage: number;
  linkTage: number;
  turnstileSite: string;
  turnstileGesetzt: boolean;
  /** Adresse des öffentlichen Formulars und der Schnittstelle für die Shop-Section */
  formular: string;
  endpunkt: string;
  /** abrechenbare Anfragen im laufenden Monat */
  monat: number;
}
